// EDEN session boundary: which signed-in account owns this page's Eden state.
// PARITY: this file must stay byte-identical between client (Cortex OS) and
// superadmin (Quantum OS).
//
// Account-owned Eden state (the conversation, the last exchange, the unsent
// draft, pending governed changes and their outcomes) belongs to ONE signed-in
// account. That account is read from the session token a request would carry:
// the server signs the token's claims and acts on them. Page context and
// stored profile keys (email, userId) can be stale, so they never decide whose
// state this is.
//
// The voice provider binds the session after checking the auth context: the
// page is signed in, and the live token names the account the auth context
// verified. Leaving a bound account (sign-out, session loss, a different
// account, a token whose account cannot be read) ENDS the session: the
// generation advances, subscribers clear account-owned state, and work that
// returns for an ended session is discarded. A refreshed token for the same
// account keeps the session.
//
// Every Eden request takes its bearer from edenSessionBearer(): the live token,
// and only while it still names the bound account. Otherwise nothing is sent,
// so a previous account's token is never reused.

// The order the auth contexts read: each portal's own key, then legacy keys.
export const EDEN_SESSION_TOKEN_KEYS = Object.freeze([
  "superadminToken",
  "clientAuthToken",
  "authToken",
  "token",
  "accessToken",
]);

let generation = 0;
let boundAccount = null;
const listeners = new Set();

function readStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage || null : null;
  } catch {
    return null;
  }
}

/** The session token the app would send right now, or null. */
export function readLiveSessionToken() {
  const storage = readStorage();
  if (!storage) return null;
  for (const key of EDEN_SESSION_TOKEN_KEYS) {
    let value = null;
    try {
      value = storage.getItem(key);
    } catch {
      return null;
    }
    const token = typeof value === "string" ? value.trim() : "";
    if (token) return token;
  }
  return null;
}

function decodeClaims(token) {
  const parts = String(token || "").split(".");
  if (parts.length !== 3 || !parts[1]) return null;
  const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const json = decodeURIComponent(
    Array.from(binary, (ch) => `%${ch.charCodeAt(0).toString(16).padStart(2, "0")}`).join("")
  );
  const claims = JSON.parse(json);
  return claims && typeof claims === "object" && !Array.isArray(claims) ? claims : null;
}

/**
 * The account a session token names, from its own claims:
 * { key, id, email, role }, or null when no account can be read.
 */
export function accountFromSessionToken(token) {
  try {
    const claims = decodeClaims(token);
    if (!claims) return null;
    const rawId = claims.id;
    const id =
      typeof rawId === "number" && Number.isFinite(rawId)
        ? String(rawId)
        : typeof rawId === "string"
          ? rawId.trim()
          : "";
    if (!id) return null;
    const role = typeof claims.role === "string" ? claims.role.trim().toLowerCase() : "";
    const email = typeof claims.email === "string" ? claims.email.trim() : "";
    return { key: `${role || "unknown"}:${id}`, id, email: email || null, role: role || null };
  } catch {
    return null;
  }
}

/**
 * Which account, if any, this page's Eden session may act for.
 *   signedIn       the auth context reports a confirmed sign-in
 *   userId, email  the identity the auth context verified with the server
 * Returns { account, reason }. account is null whenever the session must not
 * act: signed out, no token, a token whose account cannot be read, an auth
 * context that does not yet know who is signed in, or a token naming a
 * different account than the one the auth context verified (another tab
 * signed in and this page has not re-verified yet).
 */
export function resolveEdenSessionAccount({ signedIn = false, userId = null, email = null } = {}) {
  if (!signedIn) return { account: null, reason: "signed_out" };
  const token = readLiveSessionToken();
  if (!token) return { account: null, reason: "no_token" };
  const account = accountFromSessionToken(token);
  if (!account) return { account: null, reason: "unreadable_token" };
  const knownId = userId === null || userId === undefined ? "" : String(userId).trim();
  const knownEmail = typeof email === "string" ? email.trim().toLowerCase() : "";
  if (knownId) {
    if (knownId !== account.id) return { account: null, reason: "auth_state_differs" };
  } else if (knownEmail) {
    if (!account.email || account.email.toLowerCase() !== knownEmail) {
      return { account: null, reason: "auth_state_differs" };
    }
  } else {
    return { account: null, reason: "auth_identity_unknown" };
  }
  return { account, reason: null };
}

export function getEdenSessionGeneration() {
  return generation;
}

export function isCurrentEdenSession(sessionGeneration) {
  return sessionGeneration === generation;
}

export function getEdenSessionAccount() {
  return boundAccount;
}

/**
 * Bind the session to an account, or to none. The voice provider calls this.
 * Returns true when it ENDED the previous session: a bound account was left.
 */
export function syncEdenSessionAccount(account) {
  const next = account && account.key ? account : null;
  const previous = boundAccount;
  boundAccount = next;
  if (!previous || (next && next.key === previous.key)) return false;
  generation += 1;
  listeners.forEach((listener) => {
    try {
      listener(generation);
    } catch {
      // One holder's cleanup never stops the others.
    }
  });
  return true;
}

export function subscribeEdenSession(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The bearer for an Eden request: the live token, only while it names the
 * bound account. Null means do not send.
 */
export function edenSessionBearer() {
  if (!boundAccount) return null;
  const token = readLiveSessionToken();
  if (!token) return null;
  const account = accountFromSessionToken(token);
  return account && account.key === boundAccount.key ? token : null;
}

export function resetEdenSessionForTests() {
  generation = 0;
  boundAccount = null;
}
